import { useRef, useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import EmailEditor, { EditorRef, EmailEditorProps } from 'react-email-editor';
import { ArrowLeft, Save, Copy, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { getErrorMessage } from '@/lib/errors';

const ETIQUETAS = [
  { tag: '${NOMBRE}',           desc: 'Nombre del paciente' },
  { tag: '${APELLIDO}',         desc: 'Apellido paterno' },
  { tag: '${NOMBRE_COMPLETO}',  desc: 'Nombre completo' },
  { tag: '${EMAIL}',            desc: 'Correo del paciente' },
  { tag: '${TELEFONO}',         desc: 'Teléfono' },
  { tag: '${FECHA_NACIMIENTO}', desc: 'Fecha de nacimiento' },
  { tag: '${CLINICA}',          desc: 'Nombre de la clínica' },
];

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300';

function EtiquetaChip({ tag, desc }: { tag: string; desc: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(tag);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={copy}
      className="flex items-center justify-between gap-2 w-full px-3 py-2 rounded-lg border border-gray-100 hover:border-primary-200 hover:bg-primary-50 transition-colors text-left group"
    >
      <div>
        <p className="text-xs font-mono font-medium text-primary-700">{tag}</p>
        <p className="text-xs text-text-secondary">{desc}</p>
      </div>
      {copied
        ? <Check size={13} className="text-green-600 flex-shrink-0" />
        : <Copy size={13} className="text-gray-300 group-hover:text-primary-400 flex-shrink-0" />}
    </button>
  );
}

export function PlantillaEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const editorRef = useRef<EditorRef>(null);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const [editorReady, setEditorReady] = useState(false);
  const [editorHeight, setEditorHeight] = useState(600);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({ nombre: '', descripcion: '', asunto: '' });
  const f = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(p => ({ ...p, [field]: e.target.value }));

  // Medir el contenedor del editor con ResizeObserver para pasar px exactos al iframe
  useEffect(() => {
    const el = editorContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setEditorHeight(Math.floor(entry.contentRect.height));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { data: plantilla } = useQuery({
    queryKey: ['plantilla-marketing', id],
    queryFn: () => api.get(`/api/marketing/plantillas/${id}`).then(r => r.data),
    enabled: isEdit,
  });

  useEffect(() => {
    if (plantilla) {
      setForm({ nombre: plantilla.nombre, descripcion: plantilla.descripcion || '', asunto: plantilla.asunto });
    }
  }, [plantilla]);

  const onReady: EmailEditorProps['onReady'] = useCallback(() => {
    setEditorReady(true);
  }, []);

  // Cargar diseño cuando el editor esté listo Y los datos lleguen
  useEffect(() => {
    if (editorReady && plantilla?.diseno) {
      try {
        editorRef.current?.editor?.loadDesign(JSON.parse(plantilla.diseno));
      } catch {
        // diseño inválido, arranca en blanco
      }
    }
  }, [editorReady, plantilla]);

  const createMutation = useMutation({
    mutationFn: (dto: any) => api.post('/api/marketing/plantillas', dto).then(r => r.data),
    onSuccess: () => { toast.success('Plantilla creada'); navigate('/marketing/plantillas'); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al crear plantilla')),
  });
  const updateMutation = useMutation({
    mutationFn: (dto: any) => api.put(`/api/marketing/plantillas/${id}`, dto).then(r => r.data),
    onSuccess: () => { toast.success('Plantilla guardada'); navigate('/marketing/plantillas'); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al guardar plantilla')),
  });

  const handleSave = () => {
    if (!form.nombre.trim() || !form.asunto.trim()) { toast.error('Nombre y asunto son requeridos'); return; }
    if (!editorReady) { toast.error('El editor aún no está listo'); return; }
    setSaving(true);
    editorRef.current?.editor?.exportHtml((data) => {
      const dto = { ...form, diseno: JSON.stringify(data.design), html: data.html };
      if (isEdit) updateMutation.mutate(dto);
      else createMutation.mutate(dto);
      setSaving(false);
    });
  };

  return (
    // -m-5 cancela el p-5 del <main>; height: calc(100% + 40px) recupera esos 40px
    <div className="-m-5 flex flex-col" style={{ height: 'calc(100% + 40px)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100 bg-white flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary">
            <ArrowLeft size={18} />
          </button>
          <h2 className="font-semibold text-text-primary">
            {isEdit ? 'Editar plantilla' : 'Nueva plantilla'}
          </h2>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || createMutation.isPending || updateMutation.isPending || !editorReady}
          className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60 transition-colors"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Guardar plantilla
        </button>
      </div>

      {/* Cuerpo: sidebar + editor */}
      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* Sidebar */}
        <div className="w-72 flex-shrink-0 border-r border-gray-100 bg-white overflow-y-auto p-4 space-y-5">
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Datos de la plantilla</h3>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Nombre *</label>
              <input className={inputCls} value={form.nombre} onChange={f('nombre')} placeholder="Bienvenida a nuevos pacientes" />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Asunto del correo *</label>
              <input className={inputCls} value={form.asunto} onChange={f('asunto')} placeholder="Hola ${NOMBRE}, ¡bienvenido!" />
              <p className="text-xs text-text-secondary mt-1">Puedes usar etiquetas dinámicas</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Descripción</label>
              <textarea className={`${inputCls} resize-none`} rows={2} value={form.descripcion} onChange={f('descripcion')} placeholder="Descripción opcional..." />
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Etiquetas dinámicas</h3>
            <p className="text-xs text-text-secondary">Haz clic para copiar e inserta en el editor</p>
            <div className="space-y-1.5">
              {ETIQUETAS.map(e => <EtiquetaChip key={e.tag} {...e} />)}
            </div>
          </div>
        </div>

        {/* Contenedor del editor — ref mide el alto real disponible */}
        <div ref={editorContainerRef} className="relative flex-1 overflow-hidden">
          {!editorReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
              <div className="flex items-center gap-2 text-text-secondary">
                <Loader2 size={20} className="animate-spin" />
                <span className="text-sm">Cargando editor...</span>
              </div>
            </div>
          )}
          <EmailEditor
            ref={editorRef}
            onReady={onReady}
            style={{ height: editorHeight }}
            options={{
              projectId: 197411,
              locale: 'es-ES',
              features: { preview: true },
              appearance: { theme: 'light' },
              templateId: 709316,
              displayMode: "email",
              safeHtml: true,
            }}
          />
        </div>
      </div>
    </div>
  );
}
